import type { Timestamp } from 'firebase/firestore';
import type { BillingAddress, BillingPaymentPreference, ContactRole, UploadedFileMeta } from '../../types';

export const PARTNER_SCHEMA_VERSION = 1;

export type FirestoreDate = Timestamp | Date | null;

export type PartnerBranding = {
  logoUrl?: string | null;
  heroImageUrl?: string | null;
};

export type ParentCommunicationPolicy = {
  directOutreachAllowed?: boolean | null;
  parentAccountsAllowed?: boolean | null;
  parentEmailsAvailable?: boolean | null;
  partnerCanSendAccountEmail?: boolean | null;
  notes?: string | null;
};

export type PartnerDemographics = {
  avgHouseholdIncomeUsd?: number | null;
  householdIncomeBand?: 'lt_50k' | '50_100k' | '100_150k' | '150k_plus' | 'unknown' | null;
  gradeLevelsOffered?: string | null;
  reliabilityScore?: number | null;
  notes?: string | null;
};

export type PartnerBillingDefaults = {
  paymentMode?: 'stripe' | 'school_flat_fee' | 'school_enrollment' | null;
  /** Default squad rate in cents; squads may override it with their own explicit price. */
  defaultProgramPriceCents?: number | null;
  proratedAllowed?: boolean | null;
  legalName?: string | null;
  billingAddress?: BillingAddress | null;
  paymentPreference?: BillingPaymentPreference | null;
  netInvoiceDays?: number | null;
  netPaymentDays?: number | null;
  invoiceTiming?: 'beginning' | 'biweekly' | 'middle_and_end' | 'end' | null;
  billingContactId?: string | null;
  authorizedContactIds?: string[];
  revenueShare?: {
    enabled: boolean;
    /** Overture's portion of gross program revenue. Partner share is the remainder. */
    overturePercent?: number | null;
    /**
     * partner_markup: the configured price is Overture's net charge; the partner adds its margin on top.
     * partner_share_deducted: the configured price is parent-facing gross; Overture charges only its share.
     */
    chargeBasis?: 'partner_markup' | 'partner_share_deducted' | null;
  };
};

export type PartnerInsuranceRequirements = {
  coiRequired?: boolean | null;
  generalLiabilityAmountUsd?: number | null;
  additionalInsuredRequired?: boolean | null;
  workersCompRequired?: boolean | null;
  autoLiabilityRequired?: boolean | null;
  professionalLiabilityRequired?: boolean | null;
  wording?: string | null;
  notes?: string | null;
  currentCoiDocumentId?: string | null;
};

export type PartnerVendorProcess = {
  required?: boolean | null;
  registrationSoftware?: string | null;
  portalUrl?: string | null;
  vendorId?: string | null;
  status?: 'not_started' | 'in_progress' | 'approved' | 'expired' | 'not_required' | null;
  renewalDate?: string | null;
  instructions?: string | null;
  contactId?: string | null;
  documentIds?: string[];
};

export interface Partner {
  id: string;
  schemaVersion: number;
  name: string;
  slug: string;
  organizationType?: string | null;
  metro?: string | null;
  timezone: string;
  branding?: PartnerBranding;
  demographics?: PartnerDemographics;
  parentCommunicationPolicy?: ParentCommunicationPolicy;
  billingDefaults?: PartnerBillingDefaults;
  insuranceRequirements?: PartnerInsuranceRequirements;
  vendorProcess?: PartnerVendorProcess;
  primaryContactId?: string | null;
  principalContactId?: string | null;
  ptoContactId?: string | null;
  marketingContactId?: string | null;
  secretaryContactId?: string | null;
  afterschoolDirectorContactId?: string | null;
  active?: boolean;
  isPublic?: boolean;
  /**
   * Parent-facing school registration URL (MyRec, RecDesk, etc.).
   * Used on the public school page for non-Stripe programs. Locations may override.
   */
  registrationLink?: string | null;
  legacySchoolIds?: string[];
  createdAt?: FirestoreDate;
  createdBy?: string | null;
  updatedAt?: FirestoreDate;
  updatedBy?: string | null;
}

export interface PartnerLocation {
  id: string;
  schemaVersion: number;
  partnerId: string;
  name: string;
  slug: string;
  address?: string | null;
  /** Optional location override; squads otherwise inherit the partner timezone. */
  timezone?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  campusPhotoUrls?: string[];
  siteContactIds?: string[];
  registrationLink?: string | null;
  wifiUsername?: string | null;
  wifiPassword?: string | null;
  wifiRules?: string | null;
  parkingInstructions?: string | null;
  arrivalProcedure?: string | null;
  dismissalProcedure?: string | null;
  lateArrivalProcedure?: string | null;
  hallwayRules?: string | null;
  bathroomRules?: string | null;
  emergencyContactOnSite?: string | null;
  hasProjectorOrTv?: boolean | null;
  projectorConnectionInstructions?: string | null;
  techTestedOnSite?: boolean | null;
  sitesWhitelisted?: boolean | null;
  active?: boolean;
  /** Inherits partner visibility unless explicitly false. */
  isPublic?: boolean;
  legacySchoolId?: string | null;
  createdAt?: FirestoreDate;
  createdBy?: string | null;
  updatedAt?: FirestoreDate;
  updatedBy?: string | null;
}

export interface PartnerContact {
  id: string;
  displayName: string;
  roles: ContactRole[];
  roleCustom?: string | null;
  locationIds?: string[];
  email?: string | null;
  phone?: string | null;
  linkedin?: string | null;
  notes?: string | null;
  preferredChannel?: 'email' | 'phone' | 'sms' | null;
  tags?: string[];
  ownerUserId?: string | null;
  active?: boolean;
  legacyContactIds?: string[];
  createdAt?: FirestoreDate;
  createdBy?: string | null;
  updatedAt?: FirestoreDate;
  updatedBy?: string | null;
}

export type PartnerRequirementType =
  | 'basic_background_check'
  | 'health_check'
  | 'tb_test'
  | 'cpr'
  | 'epi_pen'
  | 'in_person_background_check'
  | 'fingerprinting'
  | 'state_background_check'
  | 'mandated_reporter'
  | 'concussion_protocol'
  | 'partner_safety_training'
  | 'teacher_account'
  | 'other';

export type RequirementLocationOverride = {
  required?: boolean;
  instructions?: string | null;
  estimatedDays?: number | null;
  evidenceRequired?: boolean;
};

export interface PartnerRequirement {
  id: string;
  partnerId: string;
  type: PartnerRequirementType;
  label: string;
  required: boolean;
  instructions?: string | null;
  /** Partner documents that explain or substantiate this requirement. */
  attachmentDocumentIds?: string[];
  estimatedDays?: number | null;
  evidenceRequired?: boolean;
  validityDays?: number | null;
  /** External portal used when the teacher must create a partner-managed account. */
  accountSiteUrl?: string | null;
  locationOverrides?: Record<string, RequirementLocationOverride>;
  version: number;
  active: boolean;
  legacyFieldPath?: string | null;
  createdAt?: FirestoreDate;
  createdBy?: string | null;
  updatedAt?: FirestoreDate;
  updatedBy?: string | null;
}

export type RequirementCompletionStatus =
  | 'not_started'
  | 'in_progress'
  | 'submitted'
  | 'verified'
  | 'rejected'
  | 'expired'
  | 'not_required';

export type RequirementCompletion = {
  requirementId: string;
  requirementVersion: number;
  status: RequirementCompletionStatus;
  locationIds?: string[];
  completedAt?: FirestoreDate;
  expiresAt?: FirestoreDate;
  verifiedAt?: FirestoreDate;
  verifiedBy?: string | null;
  latestEvidenceId?: string | null;
  notes?: string | null;
  /** Credentials created by this teacher for a teacher_account requirement. */
  accountUsername?: string | null;
  accountPassword?: string | null;
};

export interface PartnerTeacherClearance {
  id: string;
  partnerId: string;
  teacherId: string;
  cleared: boolean;
  missingRequirementIds: string[];
  expiringRequirementIds: string[];
  requirementStatuses: Record<string, RequirementCompletion>;
  lastReviewedAt?: FirestoreDate;
  lastReviewedBy?: string | null;
  updatedAt?: FirestoreDate;
  updatedBy?: string | null;
}

export interface PartnerClearanceEvidence {
  id: string;
  partnerId: string;
  teacherId: string;
  requirementId: string;
  locationIds?: string[];
  file: UploadedFileMeta;
  submittedAt?: FirestoreDate;
  submittedBy?: string | null;
  verifiedAt?: FirestoreDate;
  verifiedBy?: string | null;
  expiresAt?: FirestoreDate;
  notes?: string | null;
  legacyStoragePath?: string | null;
}

export type PartnerSeasonCommitment = {
  mode?: 'written' | 'verbal' | 'contract' | 'declined' | 'unknown' | null;
  statement?: string | null;
  documentId?: string | null;
  confirmedAt?: FirestoreDate;
  confirmedByContactId?: string | null;
};

export type PartnerSeasonMarketingDeliverable = {
  id: string;
  type: 'newsletter' | 'outreach' | 'demo' | 'blurb' | 'email' | 'flyer' | 'social' | 'website' | 'registration_page' | 'other';
  title: string;
  owner: 'partner' | 'overture';
  status: 'planned' | 'in_progress' | 'sent' | 'completed' | 'declined' | 'not_applicable';
  dueDate?: string | null;
  completedAt?: FirestoreDate;
  url?: string | null;
  notes?: string | null;
  /** Dates of sharing with a partner and actual delivery are distinct. */
  sharedDate?: string | null;
  activityDate?: string | null;
  completedDate?: string | null;
  contactId?: string | null;
  assignedTo?: string | null;
  outreachPurpose?: 'newsletter' | 'demo' | 'other' | null;
  channel?: 'email' | 'phone' | 'sms' | 'in_person' | 'portal' | 'other' | null;
  audience?: 'all_families' | 'current_parents' | 'past_parents' | 'students' | 'other' | null;
  programName?: string | null;
  squadIds?: string[];
  locationId?: string | null;
  content?: string | null;
  response?: string | null;
  nextAction?: string | null;
  followUpDate?: string | null;
  registrationUrl?: string | null;
  approvedBy?: string | null;
  approvedDate?: string | null;
  audienceCount?: number | null;
  engagementCount?: number | null;
  createdAt?: FirestoreDate;
  createdBy?: string | null;
  updatedAt?: FirestoreDate;
  updatedBy?: string | null;
};

export type PartnerSeasonMarketing = {
  deliverables?: PartnerSeasonMarketingDeliverable[];
  /** Legacy fields retained so existing records can be converted without data loss. */
  partnerOptions?: string[];
  plannedDeliverables?: string[];
  sentDeliverables?: string[];
  independentlyManagedDeliverables?: string[];
  statusByDeliverable?: Record<string, 'planned' | 'sent' | 'completed' | 'declined' | 'not_applicable'>;
  notes?: string | null;
};

export type PartnerSeasonHealthOverride = {
  status: 'on_track' | 'attention' | 'at_risk' | 'blocked';
  reason: string;
  expiresOn: string;
  setAt?: FirestoreDate;
  setBy?: string | null;
};

export interface OrganizationSeason {
  id: string;
  termId: string;
  label: string;
  planningPeriodStartDate: string;
  planningPeriodEndDate: string;
  registrationStartDate: string;
  registrationEndDate: string;
  defaultProgramStartDate: string;
  defaultProgramEndDate: string;
  updatedAt?: FirestoreDate;
  updatedBy?: string | null;
}

export interface PartnerSeason {
  id: string;
  partnerId: string;
  termId: string;
  label?: string | null;
  commitment?: PartnerSeasonCommitment;
  registrationStartDate?: string | null;
  registrationEndDate?: string | null;
  planningPeriodStartDate?: string | null;
  planningPeriodEndDate?: string | null;
  /** Partner-specific boundaries used to propose schedules before squads exist. */
  defaultProgramStartDate?: string | null;
  defaultProgramEndDate?: string | null;
  /** Records which values were proposed by the org season versus changed for this partner. */
  dateSources?: Partial<Record<'planningPeriodStartDate' | 'planningPeriodEndDate' | 'registrationStartDate' | 'registrationEndDate' | 'defaultProgramStartDate' | 'defaultProgramEndDate', 'org_default' | 'partner_override'>>;
  registrationLinkOverridesByLocationId?: Record<string, string>;
  marketing?: PartnerSeasonMarketing;
  healthOverride?: PartnerSeasonHealthOverride | null;
  linkedLocationIds?: string[];
  linkedSquadIds?: string[];
  createdAt?: FirestoreDate;
  createdBy?: string | null;
  updatedAt?: FirestoreDate;
  updatedBy?: string | null;
}

export type PartnerCalendarEntryKind = 'event' | 'no_class';

/**
 * A partner-level calendar entry.
 * - 'event': something happening at the partner worth knowing about (curriculum night,
 *   farmer's market, book fair). Can be converted into a draft single-day squad.
 * - 'no_class': a holiday or other day the partner does not run programming. Inherited
 *   by every recurring squad as schedule.inheritedExceptions unless the squad's
 *   location is listed in excludedLocationIds.
 */
export interface PartnerCalendarEntry {
  id: string;
  partnerId: string;
  kind: PartnerCalendarEntryKind;
  title: string;
  startDate: string; // YYYY-MM-DD
  /** Inclusive last day for multi-day entries (e.g. winter break). Defaults to startDate. */
  endDate?: string | null;
  /** HH:mm — events only; no-class days are all-day. */
  startTime?: string | null;
  endTime?: string | null;
  /** Events: optional specific location the event happens at. */
  locationId?: string | null;
  /** No-class days: locations this day does NOT apply to. */
  excludedLocationIds?: string[];
  notes?: string | null;
  /** Where the entry came from: entered by an admin or added by the AI event scraper. */
  source: 'manual' | 'ai';
  /** Set when an event has been converted into a squad. */
  convertedSquadId?: string | null;
  createdAt?: FirestoreDate;
  createdBy?: string | null;
  updatedAt?: FirestoreDate;
  updatedBy?: string | null;
}

export interface ScopedNote {
  id: string;
  title?: string | null;
  body: string;
  category?: string | null;
  attachmentDocumentIds?: string[];
  createdAt?: FirestoreDate;
  createdBy?: string | null;
  updatedAt?: FirestoreDate;
  updatedBy?: string | null;
}

export type PartnerDocumentKind =
  | 'coi'
  | 'w9'
  | 'contract'
  | 'vendor'
  | 'safety_protocol'
  | 'notes_attachment'
  | 'other';

export interface ScopedDocument {
  id: string;
  kind: PartnerDocumentKind;
  title: string;
  description?: string | null;
  file: UploadedFileMeta;
  effectiveDate?: string | null;
  expirationDate?: string | null;
  createdAt?: FirestoreDate;
  createdBy?: string | null;
}

export interface PartnerSeasonFlow {
  id: string;
  partnerId: string;
  seasonId: string;
  locationIds: string[];
  squadIds: string[];
  pipelineStage?: string | null;
  riskScoreOutOf10?: number | null;
  internalOwnerUserId?: string | null;
  intakeSnapshot?: unknown;
  appliedSources?: unknown[];
  checkpointState?: Record<string, unknown>;
  taskState?: Record<string, unknown>;
  legacyOpsFlowIds?: string[];
  createdAt?: FirestoreDate;
  createdBy?: string | null;
  updatedAt?: FirestoreDate;
  updatedBy?: string | null;
}

export interface PublicLocation {
  id: string;
  partnerId: string;
  partnerSlug: string;
  partnerName: string;
  locationSlug: string;
  locationName: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  campusPhotoUrl?: string | null;
  logoUrl?: string | null;
  registrationLink?: string | null;
  hasWorkedWith?: boolean;
  updatedAt?: FirestoreDate;
}
